import React, { useEffect, useState } from "react";
import { AppComponents, RMFlightWrapper, Mapper } from "../../../types";
import { changeSelectedFlight } from "../searchFlightsSlice";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { trackFlightDetails } from "../../analytics/AnalyticsActions";
import { selectAirlineLinks } from "../../config/ConfigSlice";
import CodeShareIcon from "./CodeShareIcon";
import LowCostIcon from "./LowCostIcon";
import SearchFlightsResultRange from "./SearchFlightsResultRange";
import { useTranslation } from "react-i18next";
import {
  selectConnectionMarkers,
  selectDirectMarkers,
} from "../../marker/markerSlice";
import FlightCheckbox from "./FlightCheckbox";
import AirlineLogo from "./AirlineLogo";

type SearchFlightsResultProps = {
  result: RMFlightWrapper;
  dateMin: Date;
  dateMax: Date;
  mapper: Mapper;
  components: AppComponents;
  layout: string;
};

function SearchFlightsResultITT({
  result,
  dateMin,
  dateMax,
  mapper,
  components,
}: SearchFlightsResultProps) {
  const dispatch = useAppDispatch();
  const airlineLinks = useAppSelector(selectAirlineLinks);
  const { t } = useTranslation();

  const selectFlight = () => {
    dispatch(changeSelectedFlight(result));
    dispatch(trackFlightDetails(result));
  };

  const generateAirlineLabel = (airlineLabel, airlineCode) => {
    const airlineLink = mapper.includeAirlineLinks
      ? airlineLinks.find((airline) => airline.code === airlineCode)
      : null;
    if (airlineLink) {
      return (
        <a
          href={`//${airlineLink.url}`}
          target="_blank"
          title={airlineLink.url}
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          {airlineLabel}
        </a>
      );
    } else {
      return airlineLabel;
    }
  };

  return (
    <div
      onClick={selectFlight}
      title={t("TITLE_CLICK_FOR_DETAILS")}
      tabIndex={0}
      className="flight-result"
      role="row"
    >
      <div className="columns is-mobile is-vcentered">
        {components.Results.copyToClipboard && (
          <div
            className="column is-narrow"
            onClick={(e) => e.stopPropagation()}
            role="cell"
          >
            <FlightCheckbox result={result} />
          </div>
        )}
        <div className="column">
          <div className="columns is-mobile is-vcentered">
            <div className="column is-narrow is-hidden-mobile" role="cell">
              <AirlineLogo airlineCode={result.operatedBy} />
            </div>
            <div className="column is-hidden-mobile is-thirty" role="cell">
              <div>
                {generateAirlineLabel(result.airline, result.operatedBy)}
              </div>
              <div>
                {result.stops === "0"
                  ? t("LABEL_FLIGHT_FINDER_NONSTOP")
                  : result.stops + " " + t("LABEL_FLIGHT_FINDER_NUMBER_STOPS")}
              </div>
            </div>
            <div className="column" role="cell">
              <div>
                <abbr
                  title={
                    result.oagOpByCarrierName || result.sadName
                      ? `${t("LABEL_OPERATED_BY")} ${
                          result.oagOpByCarrierName || result.sadName
                        } - ${result.oagOpByCarrier}${result.oagOpByFlightNo}`
                      : null
                  }
                >{`${result.operatedBy}${result.flightNo}`}</abbr>
                {result.codeshareInd && <CodeShareIcon />}
                {mapper.enableLowCostIndicator && result.lowCost && (
                  <LowCostIcon />
                )}
              </div>
              <div>
                {Math.floor(result.travelTime / 60)}h{result.travelTime % 60}m
              </div>
            </div>
            <div className="column" role="cell">
              <AirportAbbrLabel airportCode={result.depart} />
            </div>
            <div className="column" role="cell">
              <AirportAbbrLabel
                airportCode={
                  result.connxLeg1Arrival != null
                    ? result.connxLeg1Arrival
                    : result.arrival
                }
              />
            </div>
            <div className="column" role="cell">
              <div>{result.departureTimeAsString}</div>
            </div>
            <div className="column" role="cell">
              <div>
                {result.connxLeg1ArrivalTimeAsString !== null
                  ? result.connxLeg1ArrivalTimeAsString
                  : result.arrivalTimeAsString}
              </div>
            </div>
          </div>
          <SearchFlightsResultRange
            frequency={result.frequency}
            effectiveFrom={result.effectiveFrom}
            effectiveTo={result.effectiveTo}
            dateMin={dateMin}
            dateMax={dateMax}
            density="compact"
          />
          {result.connxLeg2OperatedBy !== null && (
            <>
              <div className="columns is-mobile is-vcentered flight-result-separator">
                <div className="column is-narrow is-hidden-mobile" role="cell">
                  <AirlineLogo
                    airlineCode={result.connxLeg2OperatedBy}
                    style={{
                      opacity: `${
                        result.connxLeg2OperatedBy !== result.operatedBy ? 1 : 0
                      }`,
                    }}
                  />
                </div>
                <div className="column is-hidden-mobile is-thirty" role="cell">
                  <div>
                    {generateAirlineLabel(
                      result.connxLeg2Airline,
                      result.connxLeg2OperatedBy
                    )}
                  </div>
                  <div>
                    {result.connxLeg2Stops === "0"
                      ? t("LABEL_FLIGHT_FINDER_NONSTOP")
                      : result.stops +
                        " " +
                        t("LABEL_FLIGHT_FINDER_NUMBER_STOPS")}
                  </div>
                </div>
                <div className="column" role="cell">
                  <div>
                    <abbr
                      title={
                        result.connxLeg2OagOpByCarrierName ||
                        result.connxLeg2SadName
                          ? `${t("LABEL_OPERATED_BY")} ${
                              result.connxLeg2OagOpByCarrierName ||
                              result.connxLeg2SadName
                            } - ${result.connxLeg2OagOpByCarrier}${
                              result.connxLeg2OagOpByFlightNo
                            }`
                          : null
                      }
                    >
                      {`${result.connxLeg2OperatedBy}${result.connxLeg2FlightNo}`}
                    </abbr>
                    {result.connxLeg2CodeshareInd && <CodeShareIcon />}
                    {mapper.enableLowCostIndicator &&
                      result.connxLeg2LowCost && <LowCostIcon />}
                  </div>
                  <div>
                    {Math.floor(result.connxLeg2TravelTime / 60)}h
                    {result.connxLeg2TravelTime % 60}m
                  </div>
                </div>
                <div className="column" role="cell">
                  <AirportAbbrLabel airportCode={result.connxLeg2Depart} />
                </div>
                <div className="column" role="cell">
                  <AirportAbbrLabel
                    airportCode={
                      result.connxLeg2Arrival !== null
                        ? result.connxLeg2Arrival
                        : result.arrival
                    }
                  />
                </div>
                <div className="column" role="cell">
                  <div>{result.connxLeg2DepartureTimeAsString}</div>
                </div>
                <div className="column" role="cell">
                  <div>
                    {result.connxLeg2ArrivalTimeAsString !== null
                      ? result.connxLeg2ArrivalTimeAsString
                      : result.arrivalTimeAsString}
                  </div>
                </div>
              </div>
            </>
          )}
          <SearchFlightsResultRange
            frequency={result.connxLeg2Frequency}
            effectiveFrom={result.effectiveFrom}
            effectiveTo={result.effectiveTo}
            dateMin={dateMin}
            dateMax={dateMax}
            density="compact"
          />
          {result.connxLeg3OperatedBy !== null && (
            <>
              <div className="columns is-mobile is-vcentered flight-result-separator">
                <div className="column is-narrow is-hidden-mobile" role="cell">
                  <AirlineLogo
                    airlineCode={result.connxLeg3OperatedBy}
                    style={{
                      opacity: `${
                        result.connxLeg2OperatedBy !==
                        result.connxLeg3OperatedBy
                          ? 1
                          : 0
                      }`,
                    }}
                  />
                </div>
                <div className="column is-hidden-mobile is-thirty" role="cell">
                  <div>
                    {generateAirlineLabel(
                      result.connxLeg3Airline,
                      result.connxLeg3OperatedBy
                    )}
                  </div>
                  <div>
                    {result.connxLeg3Stops === "0"
                      ? t("LABEL_FLIGHT_FINDER_NONSTOP")
                      : result.stops +
                        " " +
                        t("LABEL_FLIGHT_FINDER_NUMBER_STOPS")}
                  </div>
                </div>
                <div className="column" role="cell">
                  <div>
                    <abbr
                      title={
                        result.connxLeg3OagOpByCarrierName ||
                        result.connxLeg3SadName
                          ? `${t("LABEL_OPERATED_BY")} ${
                              result.connxLeg3OagOpByCarrierName ||
                              result.connxLeg3SadName
                            } - ${result.connxLeg3OagOpByCarrier}${
                              result.connxLeg3OagOpByFlightNo
                            }`
                          : null
                      }
                    >
                      {`${result.connxLeg3OperatedBy}${result.connxLeg3FlightNo}`}
                    </abbr>
                    {result.connxLeg3CodeshareInd && <CodeShareIcon />}
                    {mapper.enableLowCostIndicator &&
                      result.connxLeg3LowCost && <LowCostIcon />}
                  </div>
                  <div>
                    {Math.floor(result.connxLeg3TravelTime / 60)}h
                    {result.connxLeg3TravelTime % 60}m
                  </div>
                </div>
                <div className="column" role="cell">
                  <AirportAbbrLabel airportCode={result.connxLeg3Depart} />
                  <div>{result.connxLeg3Depart}</div>
                </div>
                <div className="column" role="cell">
                  {" "}
                  <AirportAbbrLabel
                    airportCode={
                      result.connxLeg3Arrival !== null
                        ? result.connxLeg3Arrival
                        : result.arrival
                    }
                  />
                </div>
                <div className="column" role="cell">
                  <div>{result.connxLeg3DepartureTimeAsString}</div>
                </div>
                <div className="column" role="cell">
                  <div>
                    {result.connxLeg3ArrivalTimeAsString !== null
                      ? result.connxLeg3ArrivalTimeAsString
                      : result.arrivalTimeAsString}
                  </div>
                </div>
              </div>
              <SearchFlightsResultRange
                frequency={result.connxLeg3Frequency}
                effectiveFrom={result.effectiveFrom}
                effectiveTo={result.effectiveTo}
                dateMin={dateMin}
                dateMax={dateMax}
                density="compact"
              />
            </>
          )}
        </div>
      </div>
      {result.connxLeg2OperatedBy !== null && (
        <>
          <div className="has-text-centered">
            <span className="has-text-weight-bold">
              {t("LABEL_FLIGHT_FINDER_TOTAL_JOURNEY")}&nbsp;
            </span>
            {Math.floor(result.totalTravelTime / 60)}h{" "}
            {result.totalTravelTime % 60}m
          </div>
        </>
      )}
    </div>
  );
}

function AirportAbbrLabel({ airportCode }) {
  const directMarkers = useAppSelector(selectConnectionMarkers);
  const connectionMarkers = useAppSelector(selectDirectMarkers);
  const [label, setLabel] = useState<string>(airportCode);

  useEffect(() => {
    let marker = directMarkers.markers.find(
      (marker) => marker.code === airportCode
    );
    if (!marker) {
      marker = connectionMarkers.markers.find(
        (marker) => marker.code === airportCode
      );
    }
    if (marker) {
      setLabel(marker.portName);
    }
  }, [airportCode, directMarkers, connectionMarkers]);

  if (airportCode === label) {
    return <div>{label}</div>;
  } else {
    return <abbr title={label}>{airportCode}</abbr>;
  }
}

export default SearchFlightsResultITT;
